'use client'

import { useSelector } from 'react-redux'
import styles from './faq.module.css'
import { Spacer, Typography, Container } from '@ct-design/lib'
import Downarrow from '@/src/assets/svgs/downarrow'
import faqStyleConfig from '@/src/constants/footer'
import { DeviceType } from '@/src/types'
import { DESKTOP } from '@/src/constants'
import {
    getFAQData,
    getQuestionAndAnswersData
} from '@/src/store/slices/visaSlice/visaRequirementsSlice'
import useVisaRequirements from '@/src/hooks/useVisaRequirements'

const FaqSection = ({ deviceType = DESKTOP }: { deviceType: DeviceType }) => {
    const {
        faqTitleVariant,
        faqSectionTitleVariant,
        faqQuestionVariant,
        faqAnswerVariant
    } = faqStyleConfig[deviceType]

    const { isLoading, faq, questionAndAnswers } =
        useVisaRequirements(deviceType)

    // Show loading state if no data is available
    if (isLoading) {
        return (
            <Container>
                <Typography
                    variant={faqTitleVariant}
                    className='flex flex-start'
                >
                    Frequently Asked Questions
                </Typography>
                <Spacer className='mb-8' />
                <Container className='flex flex-column mb-2'>
                    <Typography variant={faqAnswerVariant} color='subheading'>
                        Loading FAQ data...
                    </Typography>
                </Container>
            </Container>
        )
    }

    // Prepare categories from API data
    const faqCategories = []

    // Add FAQ section if available
    if (faq) {
        faqCategories.push({
            title: faq.Heading,
            questions: faq.Data.map((item) => ({
                question: item.Title,
                answer: item.Description
            }))
        })
    }

    // Add Q&A section if available
    if (questionAndAnswers) {
        faqCategories.push({
            title: questionAndAnswers.Heading,
            questions: questionAndAnswers.Data.map((item) => ({
                question: item.Title,
                answer: item.Description
            }))
        })
    }

    return (
        <Container>
            <Typography variant={faqTitleVariant} className='flex flex-start'>
                Frequently Asked Questions
            </Typography>
            <Spacer className='mb-8' />

            <Container className='flex flex-column mb-2'>
                {faqCategories.map((category, catIndex) => (
                    <Container key={`category-${catIndex}`} className='mb-10'>
                        <Typography
                            variant={faqSectionTitleVariant}
                            className='mb-4'
                        >
                            {category.title}
                        </Typography>

                        <Container className='flex flex-column'>
                            {category.questions.map((item, qIndex) => (
                                <details
                                    key={`question-${catIndex}-${qIndex}`}
                                    className={styles.item}
                                >
                                    <summary
                                        className='flex flex-row flex-between p-relative c-pointer py-5'
                                        style={{ listStyle: 'none' }}
                                    >
                                        <Typography
                                            variant={faqQuestionVariant}
                                            className={styles.question}
                                        >
                                            {item.question}
                                        </Typography>
                                        <span className={styles.icon}>
                                            <Downarrow />
                                        </span>
                                    </summary>
                                    <Container className={styles.answer}>
                                        <Typography
                                            variant={faqAnswerVariant}
                                            color='subheading'
                                        >
                                            {item.answer}
                                        </Typography>
                                    </Container>
                                </details>
                            ))}
                        </Container>
                    </Container>
                ))}
            </Container>
        </Container>
    )
}

export default FaqSection
